// src/store/plugins/persistence.js
//
// Vuex 4 plugin that syncs designated state keys to localStorage using
// a namespaced key format:  `${moduleName}.${stateKey}`
//
// Each store module opts in by declaring a `persist` array on its raw
// definition, e.g.:
//
//   export default {
//     namespaced: true,
//     persist: ['token', 'userId'],
//     state: () => ({ token: '', userId: '' }),
//     ...
//   };
//
// Only keys matching the `namespace.key` format are read during hydration,
// so stale flat-format keys left over from the legacy store are ignored.

/**
 * Factory — accepts a registry mapping namespace strings to raw module
 * objects so the plugin can discover each module's `persist` array.
 *
 * @param {Record<string, { persist?: string[] }>} moduleRegistry
 * @returns {(store: import('vuex').Store) => void}
 */
export function createPersistencePlugin(moduleRegistry = {}) {
  return (store) => {
    let hydrating = true;

    // ── Hydrate: localStorage → store ───────────────────────────
    for (const [namespace, rawModule] of Object.entries(moduleRegistry)) {
      const persistKeys = rawModule.persist;
      if (!Array.isArray(persistKeys) || persistKeys.length === 0) continue;

      const payload = {};
      let hasData = false;

      for (const key of persistKeys) {
        const storageKey = `${namespace}.${key}`;
        let raw;
        try {
          raw = localStorage.getItem(storageKey);
        } catch (e) {
          if (process.env.VUE_APP_PRAGYA_ENV === 'local') {
            console.warn('[persistence] localStorage.getItem failed:', storageKey, e?.message);
          }
          continue;
        }
        if (raw === null) continue;

        try {
          payload[key] = JSON.parse(raw);
          hasData = true;
        } catch {
          // Backward compatibility: old data may be stored as plain strings.
          payload[key] = raw;
          hasData = true;
          if (process.env.VUE_APP_PRAGYA_ENV === 'local') {
            console.warn('[persistence] Non-JSON value for', storageKey, '- using raw string fallback');
          }
          try {
            // Normalize legacy format so future hydrations are JSON-safe.
            localStorage.setItem(storageKey, JSON.stringify(raw));
          } catch { /* noop */ }
        }
      }

      if (hasData) {
        store.commit(`${namespace}/HYDRATE`, payload);
      }
    }

    hydrating = false;

    // ── Subscribe: store → localStorage ─────────────────────────
    store.subscribe((mutation, state) => {
      if (hydrating) return;

      const slashIdx = mutation.type.indexOf('/');
      if (slashIdx === -1) return; // root mutations don't persist

      const namespace = mutation.type.substring(0, slashIdx);
      const mutName = mutation.type.substring(slashIdx + 1);

      // HYDRATE is our own bulk-load mutation — never write it back.
      if (mutName === 'HYDRATE') return;

      const rawModule = moduleRegistry[namespace];
      if (!rawModule?.persist?.length) return;

      const moduleState = state[namespace];
      if (!moduleState) return;

      for (const key of rawModule.persist) {
        const value = moduleState[key];
        const storageKey = `${namespace}.${key}`;

        try {
          if (value == null) {
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(storageKey, JSON.stringify(value));
          }
        } catch (e) {
          if (process.env.VUE_APP_PRAGYA_ENV === 'local') {
            console.warn('[persistence] localStorage write failed:', storageKey, e?.message);
          }
        }
      }
    });
  };
}

/**
 * Remove all persisted keys belonging to a given namespace.
 * Useful when tearing down a module or during a full logout.
 *
 * @param {string} namespace — e.g. 'auth'
 */
export function clearPersistedState(namespace) {
  const prefix = `${namespace}.`;
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((k) => localStorage.removeItem(k));
}
